import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import FeedbackOutlined from "@mui/icons-material/FeedbackOutlined";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import LinkOffOutlined from "@mui/icons-material/LinkOffOutlined";
import SchoolOutlined from "@mui/icons-material/SchoolOutlined";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import { Alert, Box, Button, Chip, IconButton, Stack, Typography, alpha } from "@mui/material";
import { useState } from "react";
import { usePendo } from "../analytics/hooks/use-pendo-watch";
import { AssistantLogo } from "./assistant-logo";
import { ASSISTANTS, assistantInfo } from "./assistants";
import { DisconnectDialog } from "./disconnect-dialog";
import { shortDate } from "./short-date";
import { UrlText } from "./url-text";

import type { Assistant } from "./assistants";
import type { ConnectionUrl } from "./connection-url";
import type { EntryPoint } from "./mcp-connect-landing";
import type { AssistantSetup } from "./mcp-connect-status";
import type { SxProps, Theme } from "@mui/material";
import type { FC } from "react";

const tryAsking = [
  {
    id: "create_comms",
    icon: <EditNoteOutlined />,
    text: "Draft an email and a matching Slack post announcing our new hybrid-working policy, scheduled for 9am Tuesday.",
  },
  {
    id: "build_program",
    icon: <AccountTreeOutlined />,
    text: "Build a new-starter onboarding program around start date, with a manager prep email and a 30-day survey.",
  },
  {
    id: "engagement",
    icon: <InsightsOutlined />,
    text: "What were our five best-performing comms last quarter, and what do they have in common?",
  },
  {
    id: "training",
    icon: <SchoolOutlined />,
    text: "Find everyone who hasn’t finished compliance training and draft a Slack reminder plus a heads-up to their managers.",
  },
];

const card: SxProps<Theme> = {
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  borderRadius: 3,
};

interface McpConnectedViewProps {
  setups: Partial<Record<Assistant, AssistantSetup>>;
  connection: ConnectionUrl | null;
  agreedLine: string | null;
  onStart: (entryPoint: EntryPoint, assistant: Assistant) => void;
  onDisconnect: (assistant: Assistant) => Promise<void | undefined>;
}

export const McpConnectedView: FC<McpConnectedViewProps> = ({
  setups,
  connection,
  agreedLine,
  onStart,
  onDisconnect,
}) => {
  const pendo = usePendo();
  const [urlCopied, setUrlCopied] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<Assistant | null>(null);

  const connected = ASSISTANTS.flatMap((assistant) => {
    const completedAt = setups[assistant.id]?.completedAt;
    return completedAt ? [{ assistant, completedAt }] : [];
  });
  const available = ASSISTANTS.filter((assistant) => !setups[assistant.id]?.completedAt);
  const [next] = available;

  const copyUrl = (): void => {
    if (!connection) return;
    void navigator.clipboard.writeText(connection.url).catch(console.error);
    pendo?.track("mcp-connect-url-copied", { source: "connected_view" });
    setUrlCopied(true);
  };

  const copyPrompt = (id: string, text: string): void => {
    void navigator.clipboard.writeText(text).catch(console.error);
    pendo?.track("mcp-connect-prompt-copied", { prompt: id, source: "connected_view" });
    setCopiedPrompt(id);
  };

  return (
    <Box component="main" sx={{ bgcolor: "action.hover", minHeight: "100%" }}>
      <Stack spacing={3.5} sx={{ maxWidth: 1100, mx: "auto", px: 3, pt: 5, pb: 8 }}>
        <Stack
          direction="row"
          useFlexGap
          spacing={2}
          sx={{ flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between" }}
        >
          <Stack spacing={1} sx={{ maxWidth: 680 }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <Typography variant="h4" component="h1">
                ChangeEngine MCP
              </Typography>
              <Chip label="Beta" size="small" color="primary" />
            </Stack>
            <Typography variant="body1" color="textSecondary">
              Your AI assistants can draft Communications, build programs and report on engagement
              in this account.
            </Typography>
            {agreedLine ? (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
              >
                <VerifiedUserOutlined fontSize="small" sx={{ color: "success.main" }} />
                {agreedLine}
              </Typography>
            ) : null}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Button
              id="mcp-feedback-button"
              variant="text"
              startIcon={<FeedbackOutlined />}
              data-analytics-id="mcp-feedback-button"
            >
              Give feedback
            </Button>
            {next ? (
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => onStart("add_assistant", next.id)}
                data-analytics-id="mcp-connect-add-assistant"
              >
                Add an assistant
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <Stack component="section" spacing={1.5} aria-labelledby="mcp-connected-heading">
          <Typography
            variant="h6"
            component="h2"
            id="mcp-connected-heading"
            sx={{ fontWeight: 700 }}
          >
            Your connected assistants{" "}
            <Box component="span" sx={{ fontWeight: 500, color: "text.secondary" }}>
              · {connected.length}
            </Box>
          </Typography>
          <Box component="ul" sx={[card, { listStyle: "none", m: 0, p: 0, overflow: "hidden" }]}>
            {connected.map(({ assistant, completedAt }) => (
              <Stack
                key={assistant.id}
                component="li"
                direction="row"
                useFlexGap
                spacing={2}
                sx={{
                  flexWrap: "wrap",
                  alignItems: "center",
                  px: 2.5,
                  py: 2.25,
                  "&:not(:last-of-type)": { borderBottom: 1, borderColor: "divider" },
                }}
              >
                <AssistantLogo assistant={assistant} size={48} />
                <Stack spacing={0.5} sx={{ flex: "1 1 260px", minWidth: 0 }}>
                  <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
                    <Typography variant="subtitle1" component="span" sx={{ fontWeight: 700 }}>
                      {assistant.name}
                    </Typography>
                    <Box
                      component="span"
                      sx={(theme) => ({
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        fontSize: 12,
                        fontWeight: 600,
                        color: "success.dark",
                        bgcolor: alpha(theme.palette.success.main, 0.12),
                        borderRadius: 999,
                        px: 1.25,
                        py: 0.25,
                      })}
                    >
                      <Box
                        component="span"
                        sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "success.main" }}
                      />
                      Set up
                    </Box>
                  </Stack>
                  <Typography variant="body2" color="textSecondary">
                    You completed setup on {shortDate(completedAt)}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <Button
                    onClick={() => onStart("view_steps", assistant.id)}
                    data-analytics-id="mcp-connect-view-steps"
                  >
                    View setup steps
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LinkOffOutlined />}
                    aria-label={`Disconnect ${assistant.name}`}
                    onClick={() => setDisconnecting(assistant.id)}
                    data-analytics-id="mcp-connect-disconnect"
                  >
                    Disconnect
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Box>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            gap: 2.5,
            alignItems: "start",
          }}
        >
          <Stack
            component="section"
            spacing={1.25}
            aria-labelledby="mcp-connection-url-heading"
            sx={[card, { px: 2.75, py: 2.5 }]}
          >
            <Typography
              variant="subtitle1"
              component="h2"
              id="mcp-connection-url-heading"
              sx={{ fontWeight: 700 }}
            >
              Connection URL
            </Typography>
            {connection ? (
              <Stack
                direction="row"
                useFlexGap
                spacing={1}
                sx={{ alignItems: "center", flexWrap: "wrap" }}
              >
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    flex: "1 1 240px",
                    minWidth: 0,
                    px: 1.5,
                    py: 1.25,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 0.75,
                    bgcolor: "action.hover",
                  }}
                >
                  <UrlText connection={connection} />
                </Typography>
                <Button
                  variant="contained"
                  startIcon={urlCopied ? <Check /> : <ContentCopyOutlined />}
                  onClick={copyUrl}
                  data-analytics-id="mcp-connect-copy-url"
                >
                  {urlCopied ? "Copied" : "Copy URL"}
                </Button>
              </Stack>
            ) : (
              <Alert severity="info">Contact your ChangeEngine team for your connection URL</Alert>
            )}
            <Typography variant="caption" color="textSecondary">
              Share it with an admin or a teammate who needs to add ChangeEngine to their own
              assistant.
            </Typography>
          </Stack>

          <Stack
            component="section"
            spacing={1.5}
            aria-labelledby="mcp-add-another-heading"
            sx={[card, { px: 2.75, py: 2.5 }]}
          >
            <Typography
              variant="subtitle1"
              component="h2"
              id="mcp-add-another-heading"
              sx={{ fontWeight: 700 }}
            >
              Add another assistant
            </Typography>
            {available.length > 0 ? (
              <Stack component="ul" spacing={1.25} sx={{ listStyle: "none", m: 0, p: 0 }}>
                {available.map((assistant) => (
                  <Stack
                    key={assistant.id}
                    component="li"
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: "center" }}
                  >
                    <AssistantLogo assistant={assistant} size={36} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {assistant.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {assistant.plan}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      aria-label={`Set up ${assistant.name}`}
                      onClick={() => onStart("add_another", assistant.id)}
                      data-analytics-id="mcp-connect-add-another"
                    >
                      Set up
                    </Button>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="textSecondary">
                You’ve set up Claude, ChatGPT and Microsoft Copilot.
              </Typography>
            )}
          </Stack>
        </Box>

        <Stack component="section" spacing={1.5} aria-labelledby="mcp-try-asking-heading">
          <Typography
            variant="subtitle1"
            component="h2"
            id="mcp-try-asking-heading"
            sx={{ fontWeight: 700 }}
          >
            Try asking
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: 1.5,
            }}
          >
            {tryAsking.map((prompt) => (
              <Stack
                key={prompt.id}
                direction="row"
                spacing={1.5}
                sx={[card, { borderRadius: 2.5, px: 2, py: 1.75, alignItems: "flex-start" }]}
              >
                <Box sx={{ color: "primary.main", display: "inline-flex", mt: 0.25 }}>
                  {prompt.icon}
                </Box>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  “{prompt.text}”
                </Typography>
                <IconButton
                  aria-label="Copy prompt"
                  size="small"
                  onClick={() => copyPrompt(prompt.id, prompt.text)}
                  data-analytics-id="mcp-connect-copy-prompt"
                  sx={{ border: 1, borderColor: "divider", color: "primary.main" }}
                >
                  {copiedPrompt === prompt.id ? (
                    <Check fontSize="small" />
                  ) : (
                    <ContentCopyOutlined fontSize="small" />
                  )}
                </IconButton>
              </Stack>
            ))}
          </Box>
        </Stack>

        <Typography variant="caption" color="textSecondary">
          This page shows your own setup. Teammates see theirs. To stop an assistant using
          ChangeEngine, remove the ChangeEngine connector in that assistant.
        </Typography>
      </Stack>
      {disconnecting ? (
        <DisconnectDialog
          assistant={assistantInfo(disconnecting)}
          isLast={connected.length === 1}
          onDisconnect={async () => {
            await onDisconnect(disconnecting);
            pendo?.track("mcp-connect-disconnected", { assistant: disconnecting });
          }}
          onClose={() => setDisconnecting(null)}
        />
      ) : null}
    </Box>
  );
};
